import { Button, DatePicker, Tree } from 'antd';
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import ActivityPreview from '../panels/ActivityPreview';
import CTX from '../../lib/AssignmentsContext';
import EditableText from '../parameters/EditableText';
const TasksTree = ({
  editable = false,
  selected,
  setSelected
}, ref) => {
  const {
    data,
    actions
  } = useContext(CTX);
  const [draggedNode, setDraggedNode] = useState();
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [checked, setChecked] = useState(selected ?? []);
  const [preview, setPreview] = useState();
  useEffect(() => {
    setChecked(selected);
  }, [selected, setSelected]);
  useEffect(() => {
    setSelected?.(checked);
  }, [checked, setSelected]);
  const treeData = data.assignments.map(({
    data: assignment
  }, index) => ({
    checkable: editable,
    selectable: false,
    isAssignment: true,
    assignmentKey: index,
    key: String(index),
    title: <span className="playlist-tree-title">
				{!editable ? `${assignment.label} ` : <EditableText value={assignment.label} onChange={v => assignment.label = v} />}
				<div style={{
        width: '80px',
        display: 'inline-block'
      }} className="helper-text">
					({assignment.tasks.length} {assignment.tasks.length == 1 ? 'activity' : 'activities'})
				</div>
				<div className="inline-datepicker">
					{'Assignment Active Dates: '}
					{!editable ? assignment.startDate.format('MM/DD/YYYY') : <DatePicker placeholder="Start Date" format="MM/DD/YYYY" onChange={v => assignment.startDate = v} value={assignment.startDate} size="small" />}
					{' - '}
					{!editable ? assignment.endDate.format('MM/DD/YYYY') : <DatePicker style={assignment.startDate?.isAfter(assignment.endDate) ? {
          border: '1px solid red'
        } : {}} size="small" placeholder="End Date" format="MM/DD/YYYY" onChange={v => assignment.endDate = v} value={assignment.endDate} disabledDate={v => v.isBefore(assignment.startDate)} />}
				</div>
			</span>,
    children: assignment.tasks.map(({
      name,
      previewURL,
      contentType
    }, i) => ({
      checkable: editable,
      selectable: false,
      isAssignment: false,
      assignmentKey: [index, i],
      key: `${index}-${i}`,
      title: <>
					{name}{' - '}
					<span className="subtitle">
						<EditableText value={assignment.instructions[i]} onChange={v => assignment.instructions[i] = v} />
					</span>
					<Button type="ghost" onClick={e => {
          e.stopPropagation();
          setPreview({
            tag: contentType,
            title: name,
            previews: [{
              url: previewURL
            }]
          });
        }}>
						Preview
					</Button>
				</>
    }))
  }));
  const allTreeKeys = [...treeData.map(c => c.key), ...treeData.flatMap(c => c.children.map(t => t.key))];
  const reorder = ({
    dropPosition,
    dragNode,
    node: dropNode
  }) => {
    if (dragNode.isAssignment) {
      const dragAssignmentIndex = dragNode.assignmentKey;
      const dropAssignmentIndex = dropPosition < dragAssignmentIndex ? dropPosition : dropPosition - 1;
      actions.reorderAssignments(dragAssignmentIndex, dropAssignmentIndex);
    } else {
      const dragIndex = dragNode.assignmentKey[1];
      const targetAssignment = dropNode.isAssignment ? dropNode.assignmentKey : dropNode.assignmentKey[0];
      if (dragNode.assignmentKey[0] !== targetAssignment) {
        const movedFromAssignment = data.assignments[dragNode.assignmentKey[0]].data;
        const movedToAssignment = data.assignments[targetAssignment].data;
        let moved = movedFromAssignment.tasks[dragIndex];
        movedFromAssignment.tasks = movedFromAssignment.tasks.filter((_, i) => i !== dragIndex);
        let tmpArray = movedToAssignment.tasks.slice(0);
        tmpArray.splice(dropNode.isAssignment ? 0 : dropPosition, 0, moved);
        movedToAssignment.tasks = tmpArray;
        moved = movedFromAssignment.instructions[dragIndex];
        movedFromAssignment.instructions = movedFromAssignment.instructions.filter((_, i) => i !== dragIndex);
        tmpArray = movedToAssignment.instructions.slice(0);
        tmpArray.splice(dropNode.isAssignment ? 0 : dropPosition, 0, moved);
        movedToAssignment.instructions = tmpArray;
      } else {
        if (dropPosition === dragIndex) {
          return;
        }
        const assignment = data.assignments[targetAssignment].data;
        const dropIndex = dropPosition < dragIndex ? dropPosition : dropPosition - 1;
        let moved = assignment.tasks[dragIndex];
        let tmpArray = assignment.tasks.filter((_, i) => i !== dragIndex);
        tmpArray.splice(dropIndex, 0, moved);
        assignment.tasks = tmpArray;
        moved = assignment.instructions[dragIndex];
        tmpArray = assignment.instructions.filter((_, i) => i !== dragIndex);
        tmpArray.splice(dropIndex, 0, moved);
        assignment.instructions = tmpArray;
      }
    }
  };
  useImperativeHandle(ref, () => ({
    checkedCount: () => checked.length,
    areAllSelected: () => allTreeKeys.length > 0 && allTreeKeys.every(k => checked.includes(k)),
    areSomeSelected: () => checked.length > 0,
    checkAll: all => setChecked(all ? allTreeKeys : []),
    removeSelected: () => {
      data.assignments.forEach(({
        data: assignment
      }, index) => {
        const indexesToRemove = treeData[index].children.filter(t => checked.includes(t.key)).map(t => t.assignmentKey[1]);
        assignment.instructions = assignment.instructions.filter((_, i) => !indexesToRemove.includes(i));
        assignment.tasks = assignment.tasks.filter((_, i) => !indexesToRemove.includes(i));
      });
      actions.removeAssignments(checked.map(Number));
      setChecked([]);
    }
  }));
  return <>
			<Tree treeData={treeData} blockNode defaultExpandAll expandedKeys={draggedNode?.isAssignment ? [] : expandedKeys} onExpand={draggedNode?.isAssignment ? null : setExpandedKeys} selectable={false} checkable checkStrictly checkedKeys={checked} onCheck={({
      checked
    }) => setChecked(checked)} draggable={editable} onDrop={reorder} onDragStart={({
      node
    }) => setDraggedNode(node)} onDragEnd={() => setDraggedNode()} allowDrop={!draggedNode ? false : ({
      dropNode,
      dropPosition
    }) => draggedNode.isAssignment ? dropNode.isAssignment && (dropPosition === 1 || dropPosition === -1 && dropNode.assignmentKey === 0) : dropNode.isAssignment ? dropPosition === 0 : dropPosition === 1} data-sentry-element="Tree" data-sentry-source-file="TasksTree.jsx" />
			<ActivityPreview {...preview} onClose={() => setPreview()} data-sentry-element="ActivityPreview" data-sentry-source-file="TasksTree.jsx" />
		</>;
};
export default forwardRef(TasksTree);